import {
  useGetDatasetQuestionsQuery,
  useGetDatasetQuestionVariablesByElementQuery,
} from "@store/api/dataset-api";
import type { VisualizationBaseCondition } from "@models/VisualizationBase";

/**
 * Whether a single base condition references a question, variable, or value
 * that is now hidden in the dataset. Used to drive the "this base uses
 * hidden sources" warning — distinct from the inline per-item badges shown
 * in BaseConditionSummaryCard/BaseDefinitionConditionSummary, which mark the
 * specific hidden item rather than the base as a whole.
 *
 * @param knownQuestionHidden - When the caller already knows the question's
 *  hidden status (e.g. a catalog that batched every question's hidden flag
 *  into one request), pass it here to skip this hook's own per-condition
 *  question fetch entirely. Omit to fetch it internally as before.
 */
export function useBaseConditionHiddenStatus(
  datasetId: string,
  condition: VisualizationBaseCondition,
  knownQuestionHidden?: boolean,
): boolean {
  const { data: questionData } = useGetDatasetQuestionsQuery(
    { id: datasetId, dataset_element_ids: [condition.questionId], page_size: 1 },
    {
      skip:
        !datasetId || !condition.questionId || knownQuestionHidden !== undefined,
    },
  );
  const { data: variablesData } = useGetDatasetQuestionVariablesByElementQuery(
    { id: datasetId, dataset_element_id: condition.questionId },
    { skip: !datasetId || !condition.questionId },
  );

  const isQuestionHidden =
    knownQuestionHidden !== undefined
      ? knownQuestionHidden
      : !!questionData?.questions?.[0]?.hidden;
  if (isQuestionHidden) return true;

  const variables = variablesData?.variables ?? [];
  const values = variablesData?.values ?? [];
  const hasHiddenVariable = variables.some(
    (v) => condition.variableIds.includes(v.datasetElementId) && v.hidden,
  );
  const hasHiddenValue = values.some(
    (v) => condition.valueIds.includes(v.datasetElementId) && v.hidden,
  );
  return hasHiddenVariable || hasHiddenValue;
}
